import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [agentSites, setAgentSites] = useState([]);

  useEffect(() => {
    loadAgents();
    loadAgentSites();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await api.get('/agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadAgentSites = async () => {
    try {
      const response = await api.get('/agent-sites/public/list').catch(() => null);
      // fallback: fetch known slugs one by one is expensive; instead do a single public lookup
      // We'll try fetching each known agent by name-slug lazily via a helper below
      // For now, just probe: attempt GET /agent-sites/{slug} for each rendered agent
      // Simpler: store empty array — profile links only appear when slug exists
      if (response?.data) setAgentSites(response.data);
    } catch {
      // no-op
    }
  };

  const slugFor = (name) => name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
  const hasWebsite = (name) => agentSites.some((s) => s.slug === slugFor(name));

  // Hardcoded leadership and Auri
  const leadership = [
    {
      id: 'benjamin',
      name: 'Benjamin Wright',
      title: 'Principal Broker / Owner',
      email: 'benjamin@bespokerealty.net',
      phone: '949-444-3544',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/eeqmxv1q_Benjamin.jpg',
      bio: 'Leading with vision, integrity, and a commitment to excellence in real estate. Benjamin brings years of expertise and a strategic approach to guiding Bespoke Realty and its agents to success.',
      imagePosition: 'object-top'
    },
    {
      id: 'telesa',
      name: 'Telesa Wright',
      title: 'Managing Broker',
      email: 'telesa@bespokerealty.net',
      phone: '949-444-3548',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/gkk4hvl1_Telesa.jpg',
      bio: 'Guiding our team with expertise, faith, and personalized client care. Telesa\'s attention to detail and genuine care for people makes her an invaluable asset to every transaction.',
      imagePosition: 'object-[center_20%]'
    }
  ];

  // Office Support Team
  const officeTeam = [
    {
      id: 'fannie',
      name: 'Fannie Coleman',
      title: 'Office Receptionist',
      email: 'fannie@bespokerealty.net',
      phone: '901-390-4414',
      photo: 'https://customer-assets.emergentagent.com/job_5290d281-24fe-4df4-8f7b-f48fa4c792c6/artifacts/5pv3ytvk_Gemini_Generated_Image_ys2x5ys2x5ys2x5y%20-%20Edited.png',
      bio: 'Fannie is the welcoming voice of Bespoke Realty. She greets every client with warmth, answers calls, and keeps the office running smoothly so our team can focus on you.',
      hours: 'Monday – Friday · 10:30 AM – 4:30 PM',
      imagePosition: 'object-top'
    }
  ];

  const auriProfile = {
    id: 'auri',
    name: 'Auri',
    title: 'AI Transaction Coordinator',
    email: null,
    phone: null,
    photo: 'https://customer-assets.emergentagent.com/job_5290d281-24fe-4df4-8f7b-f48fa4c792c6/artifacts/fw2biquj_ChatGPT%20Image%20May%2023%2C%202026%2C%2002_09_38%20PM.png',
    bio: 'Auri is Bespoke Realty\'s intelligent Transaction Coordinator — powered by AI to manage timelines, send reminders, and ensure every transaction stays seamless.',
    isAI: true
  };

  const novaProfile = {
    id: 'nova',
    name: 'Nova',
    title: 'AI Concierge',
    email: null,
    phone: null,
    photo: 'https://customer-assets.emergentagent.com/job_5290d281-24fe-4df4-8f7b-f48fa4c792c6/artifacts/4wrpbx58_ChatGPT%20Image%20May%2023%2C%202026%2C%2002_09_50%20PM.png',
    bio: 'Nova is Bespoke Realty\'s warm, boutique AI concierge — welcoming visitors, answering questions, and connecting buyers and sellers with the right agent for their journey.',
    isAI: true
  };

  // Arlo removed per user request

  // Full Agent Team
  const fullAgentTeam = [
    {
      id: 'domonique',
      name: 'Domonique Allen',
      title: 'MS Realtor®',
      email: 'realestate@domoniqueallen.com',
      phone: '(662) 660-6390',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/pf9krw7v_Dom.jpg',
      bio: 'Bringing professionalism and market expertise to Mississippi real estate transactions.',
      specialties: ['Residential Sales', 'Market Analysis']
    },
    {
      id: 'kenya',
      name: 'Kenya Jones',
      title: 'MS & TN Realtor®',
      email: 'Kenya@BespokeRealty.net',
      phone: '(901) 238-7628',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/v774qk6h_Ken.jpeg',
      bio: 'Passionate about helping clients find their perfect home across Mississippi and Tennessee.',
      specialties: ['Residential Sales', 'Investment Properties']
    },
    {
      id: 'monique',
      name: 'Monique Reed',
      title: 'MS & TN Realtor®',
      email: 'Monique@haus1216.com',
      phone: '(813) 735-0166',
      photo: 'https://customer-assets.emergentagent.com/job_property-finder-378/artifacts/a4h49ihm_Untitled%20design%2831%29.png',
      bio: 'Committed to delivering outstanding real estate service across Mississippi and Tennessee.',
      specialties: ['Residential Sales', 'Buyer Representation']
    },
    {
      id: 'yolanda',
      name: 'Yolanda Gatewood',
      title: 'MS & TN Realtor®',
      email: 'Yolanda@BespokeRealty.net',
      phone: '(901) 219-4861',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/p8jxl8ca_454A8750%281%29.jpg',
      bio: 'Dedicated to providing exceptional service to clients throughout Mississippi and Tennessee.',
      specialties: ['Residential Sales', 'First-Time Buyers']
    },
    {
      id: 'tony',
      name: 'Tony Milam',
      title: 'MS Realtor®',
      email: 'Amilam29@gmail.com',
      phone: '(901) 292-5727',
      photo: 'https://customer-assets.emergentagent.com/job_property-finder-378/artifacts/rp28m7pm_6mgNqcH3.jpg',
      bio: 'Committed to excellence in Mississippi real estate with personalized service for every client.',
      specialties: ['Residential Sales', 'Luxury Homes']
    },
    {
      id: 'emanuel',
      name: 'Emanuel Armstrong Sr.',
      title: 'MS Realtor®',
      email: 'Earmstrong@bespokerealty.net',
      phone: '(352) 234-0286',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/tzp5xohu_Emanuel.jpg',
      bio: 'Bringing expertise and dedication to every real estate transaction in Mississippi.',
      specialties: ['Residential Sales', 'Relocation']
    },
    {
      id: 'evelyn',
      name: 'Evelyn Armstrong',
      title: 'MS Realtor®',
      email: 'Earmstrong@bespokerealty.net',
      phone: '(901) 494-7530',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/q2mbwqsj_Evelyn.jpg',
      bio: 'Dedicated to helping clients navigate the Mississippi real estate market with confidence.',
      specialties: ['Residential Sales', 'New Construction']
    },
    {
      id: 'latrecie',
      name: 'Latrecie Moore',
      title: 'MS & TN Realtor®',
      email: 'soldbylatrecialashae@gmail.com',
      phone: '(901) 549-4932',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/w7al64gr_Latrice.jpg',
      bio: 'Passionate about helping Mississippi residents find their dream homes and investment properties.',
      specialties: ['Residential Sales', 'Property Investment']
    },
    {
      id: 'angela',
      name: 'Angela Majors',
      title: 'MS & TN Licensed Realtor®',
      email: 'Angelsmsells2004@yahoo.com',
      phone: '(901) 301-1337',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/m4831i51_Angela.jpeg',
      bio: 'Dedicated Tennessee real estate professional focused on personalized service and client satisfaction.',
      specialties: ['Residential Sales', 'Tennessee Market Expert']
    },
    {
      id: 'shannon',
      name: 'Shannon Dabney',
      title: 'MS & TN Realtor®',
      email: 'letscloseitbyshannon@gmail.com',
      phone: '(901) 364-9780',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/xtaqn9n5_Shannon.jpeg',
      bio: 'Expert negotiator helping clients close on their perfect homes throughout Mississippi and Tennessee.',
      specialties: ['Residential Sales', 'Closing Specialist']
    },
    {
      id: 'velinda',
      name: 'Velinda Humphrey',
      title: 'TN Realtor®',
      email: 'Velinda37@gmail.com',
      phone: '(901) 275-6714',
      photo: 'https://customer-assets.emergentagent.com/job_tailored-homes/artifacts/8wxp6587_Velinda.jpg',
      bio: 'Dedicated Tennessee real estate professional committed to exceeding client expectations.',
      specialties: ['Residential Sales', 'Tennessee Specialist']
    },
    {
      id: 'sharon',
      name: 'Sharon Thornton',
      title: 'MS & TN Realtor®',
      email: 'sthorntonrealtor@gmail.com',
      phone: '(901) 634-1770',
      photo: 'https://customer-assets.emergentagent.com/job_property-finder-378/artifacts/lbgzxohs_1000024493.jpg',
      bio: 'Dedicated real estate professional with nearly a decade of experience specializing in new construction and existing homes.',
      specialties: ['New Construction', 'Residential Sales', 'Buyer Representation']
    },
    {
      id: 'charlene',
      name: 'Charlene Bailey',
      title: 'MS & TN Real Estate Broker',
      email: 'charlenebaileysellsrealestate.com',
      phone: '(901) 359-6136',
      photo: 'https://customer-assets.emergentagent.com/job_property-finder-378/artifacts/a6q2sqt5_1000019683_09bd2317c40d1345c340563ecac0fc4a.jpg',
      bio: 'Licensed Real Estate Broker serving Mississippi and Tennessee with dedication and expertise.',
      specialties: ['Brokerage', 'Residential Sales', 'Buyer Representation']
    },
    {
      id: 'jay',
      name: 'Jay McMiller',
      title: 'MS & TN Realtor®',
      email: 'JayMcmiller@gmail.com',
      phone: '(901) 413-9950',
      photo: 'https://customer-assets.emergentagent.com/job_property-finder-378/artifacts/q6zwopl3_jay%20photo.png',
      bio: 'Dedicated real estate professional serving clients across Mississippi and Tennessee with integrity and expertise.',
      specialties: ['Residential Sales', 'Buyer Representation'],
      objectPosition: 'center 15%'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="agents-page">
      <SEO 
        title="Our Real Estate Agents | Memphis, TN & Mississippi Realtors"
        description="Meet the experienced real estate professionals at Bespoke Realty. Our agents serve Memphis, Southaven, Olive Branch, Collierville, Germantown, and all of North Mississippi and Tennessee."
        keywords="Memphis real estate agents, Mississippi realtors, Tennessee real estate team, Southaven realtor, Olive Branch real estate agent, Bespoke Realty agents"
      />
      <StructuredData schema={[
        schemas.teamList((agentSites?.length ? agentSites : agents).map(a => ({ name: a.name, slug: a.slug || a.id, photo: a.photo, title: a.title }))),
        schemas.breadcrumb([{ name: 'Home', url: '/' }, { name: 'Our Agents', url: '/agents' }]),
      ]} />
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/4fqwjzhs_teamphoto.png)',
          }}
        ></div>
        <div className="relative z-20 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-4 text-white" data-testid="agents-heading">Meet Our Team</h1>
          <p className="text-xl text-white">Experienced professionals dedicated to your success</p>
        </div>
      </section>

      {/* Leadership Message */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Excellence Through Teamwork</h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-8">
            At Bespoke Realty, our agents are more than real estate professionals—they're your partners 
            in achieving your real estate goals. Each member of our team brings unique expertise, local 
            knowledge, and a commitment to personalized service that sets us apart.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            Led by Principal Broker Benjamin Wright and Managing Broker Telesa Wright, our team embodies 
            the values of excellence, faith, and leadership in every transaction.
          </p>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Leadership Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            {leadership.map((leader) => (
              <div key={leader.id} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                <div className="h-80 bg-gray-200 overflow-hidden">
                  <img 
                    src={leader.photo} 
                    alt={leader.name}
                    className={`w-full h-full object-cover ${leader.imagePosition || 'object-top'}`}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-2">{leader.name}</h3>
                  <p className="text-[#BCA480] font-semibold text-lg mb-3">{leader.title}</p>
                  <p className="text-gray-600 mb-4">{leader.bio}</p>
                  <div className="space-y-2">
                    <a href={`mailto:${leader.email}`} className="flex items-center text-sm text-gray-600 hover:text-[#BCA480]">
                      <Mail className="h-4 w-4 mr-2" />
                      {leader.email}
                    </a>
                  </div>
                  {hasWebsite(leader.name) && (
                    <Link
                      to={`/agents/${slugFor(leader.name)}`}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#BCA480] hover:text-[#a89470] group"
                      data-testid={`view-profile-${leader.id}`}
                    >
                      Agent Website
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Office Team */}
      <section className="py-16 bg-white" data-testid="office-team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Office Team</h2>
          <div className="grid grid-cols-1 max-w-md mx-auto">
            {officeTeam.map((member) => (
              <div key={member.id} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow" data-testid={`office-card-${member.id}`}>
                <div className="h-96 bg-[#faf8f4] overflow-hidden flex items-center justify-center">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className={`w-full h-full ${member.imagePosition || 'object-cover object-top'}`}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-2">{member.name}</h3>
                  <p className="text-[#BCA480] font-semibold text-lg mb-3">{member.title}</p>
                  <p className="text-gray-600 mb-4">{member.bio}</p>
                  {member.hours && (
                    <div className="bg-[#faf8f4] border-l-4 border-[#BCA480] px-4 py-2 mb-4 rounded-r">
                      <p className="text-sm text-gray-700"><strong className="text-[#434345]">Available:</strong> {member.hours}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <a href={`tel:${member.phone}`} className="flex items-center text-sm text-gray-600 hover:text-[#BCA480]" data-testid={`office-${member.id}-phone`}>
                      <Phone className="h-4 w-4 mr-2" />
                      {member.phone}
                    </a>
                    <a href={`mailto:${member.email}`} className="flex items-center text-sm text-gray-600 hover:text-[#BCA480]" data-testid={`office-${member.id}-email`}>
                      <Mail className="h-4 w-4 mr-2" />
                      {member.email}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agent Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12" data-testid="agent-grid-heading">Our Agents</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="agent-grid">
            {fullAgentTeam.map((agent) => (
              <div key={agent.id} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow" data-testid={`agent-card-${agent.id}`}>
                <div className={`${agent.useContain ? 'h-80' : 'h-72'} bg-gray-200 overflow-hidden`}>
                  <img 
                    src={agent.photo} 
                    alt={agent.name}
                    className={`w-full h-full ${agent.useContain ? 'object-contain' : 'object-cover'}`}
                    style={{ objectPosition: agent.objectPosition || 'center top' }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-2">{agent.name}</h3>
                  <p className="text-[#BCA480] font-semibold mb-3">{agent.title}</p>
                  {agent.specialties && agent.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {agent.specialties.map((specialty, index) => (
                        <span key={index} className="text-xs bg-[#BCA480]/20 text-[#BCA480] px-2 py-1 rounded">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-gray-600 mb-4">{agent.bio}</p>
                  <div className="space-y-2">
                    <a href={`mailto:${agent.email}`} className="flex items-center text-sm text-gray-600 hover:text-[#BCA480]">
                      <Mail className="h-4 w-4 mr-2" />
                      {agent.email}
                    </a>
                    <a href={`tel:${agent.phone}`} className="flex items-center text-sm text-gray-600 hover:text-[#BCA480]">
                      <Phone className="h-4 w-4 mr-2" />
                      {agent.phone}
                    </a>
                  </div>
                  {hasWebsite(agent.name) && (
                    <Link
                      to={`/agents/${slugFor(agent.name)}`}
                      className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#BCA480] hover:text-[#a89470] group"
                      data-testid={`view-profile-${agent.id}`}
                    >
                      Agent Website
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Team - Auri & Nova */}
      <section className="py-16 bg-gradient-to-br from-[#BCA480]/5 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Meet Our AI Team</h2>
            <p className="text-lg text-gray-600">Innovation powered by intelligence, creativity, and precision</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Auri Card */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow" data-testid="auri-card">
              <div className="relative h-80 bg-gray-200 overflow-hidden">
                <img 
                  src={auriProfile.photo} 
                  alt={auriProfile.name}
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-4 right-4 bg-[#BCA480] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  AI
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-3xl font-bold mb-2">{auriProfile.name}</h3>
                <p className="text-[#BCA480] font-semibold text-xl mb-4">{auriProfile.title}</p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {auriProfile.bio}
                </p>
              </div>
            </div>

            {/* Nova Card */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow" data-testid="nova-card">
              <div className="relative h-80 bg-gray-200 overflow-hidden">
                <img 
                  src={novaProfile.photo} 
                  alt={novaProfile.name}
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-4 right-4 bg-[#BCA480] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  AI
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-3xl font-bold mb-2">{novaProfile.name}</h3>
                <p className="text-[#BCA480] font-semibold text-xl mb-4">{novaProfile.title}</p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {novaProfile.bio}
                </p>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div className="text-center mt-12">
            <p className="text-xl text-gray-700 font-semibold italic">
              Building the Framework for Bespoke Excellence
            </p>
          </div>
        </div>
      </section>

      {/* Culture Video Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Our Team Culture</h2>
          <p className="text-lg text-gray-600 mb-8">
            At Bespoke Realty, we believe in collaboration, continuous growth, and supporting each other 
            to provide the best service to our clients. Watch our team message below:
          </p>
          <div className="bg-gray-100 rounded-lg p-8">
            <p className="text-gray-500 italic">"Together, we create extraordinary experiences for our clients. Our strength lies in our diversity, our commitment to excellence, and our shared vision of making real estate dreams come true."</p>
            <p className="text-[#BCA480] font-bold mt-4">- Benjamin & Telesa Wright</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Agents;
